import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Header";

const Frame = ({ children }) => (
  <div className="min-h-screen grid place-items-center grid-bg px-4">
    <div className="bg-card border border-border rounded-sm p-10 max-w-md w-full text-center space-y-5">
      <div className="flex justify-center"><Logo /></div>
      {children}
    </div>
  </div>
);

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState("checking");
  const tries = useRef(0);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return setState("failed");
    let stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { await checkAuth(); setState("done"); setTimeout(() => navigate("/app"), 1800); return; }
        if (["failed", "expired"].includes(data.status)) return setState("failed");
      } catch { /* keep polling */ }
      if (!stop && ++tries.current < 15) setTimeout(poll, 2000); else if (!stop) setState("failed");
    };
    poll();
    return () => { stop = true; };
  }, [params, checkAuth, navigate]);

  return (
    <Frame>
      {state === "checking" && <><Loader2 className="h-10 w-10 mx-auto animate-spin text-primary" /><p data-testid="payment-checking">Confirming your subscription…</p></>}
      {state === "done" && <><CheckCircle2 className="h-10 w-10 mx-auto text-emerald-400" /><p className="font-display text-3xl uppercase" data-testid="payment-success">You're all set</p><p className="text-sm text-muted-foreground">Taking you to your shop…</p></>}
      {state === "failed" && <><XCircle className="h-10 w-10 mx-auto text-red-400" /><p data-testid="payment-failed">We couldn't confirm the payment yet.</p><Link to="/app/billing" className="text-primary" data-testid="payment-back-billing">Back to billing</Link></>}
    </Frame>
  );
}

export function PaymentCancel() {
  return (
    <Frame>
      <XCircle className="h-10 w-10 mx-auto text-muted-foreground" />
      <p data-testid="payment-cancel">Checkout canceled. No charge was made.</p>
      <Link to="/app/billing" className="text-primary font-semibold" data-testid="payment-cancel-back">Back to billing</Link>
    </Frame>
  );
}
